/**
 * DealLossReasonModal - Modal exigido ao marcar um negócio como perdido
 *
 * Sempre abre quando o usuário clica em "Marcar como perdido". Exige seleção
 * de um motivo cadastrado (crm_loss_reasons). Quando o pipeline tem 2+ fases
 * `is_lost`, exige também a fase de destino. Permite anotação opcional.
 *
 * RELEASE: 2026-08-07 v1.2.1 - Corrige reset indevido do formulário (motivo
 *          "desmarcando") causado por re-renders do pai: reset agora ocorre
 *          apenas na transição fechado -> aberto e a pré-seleção de conversas
 *          é aplicada uma única vez por abertura.
 * RELEASE: 2026-08-07 v1.2.0 - Exibe o ícone do canal em cada conversa ativa,
 *          reutilizando a regra visual da listagem de inboxes.
 * RELEASE: 2026-08-07 v1.1.0 - Lista as conversas ativas do contato vinculadas
 *          ao vendedor do negócio e permite encerrá-las junto com a perda.
 * RELEASE: 2026-07-10 v1.0.0
 */
import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { getChannelVisual } from '@/components/tenant/inbox/channelVisuals';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { XCircle, Loader2, Plus, MessageSquare } from 'lucide-react';
import { useCrmLossReasons } from '@/hooks/useCrmLossReasons';
import { LossReasonFormDialog } from '@/components/crm/loss-reasons/LossReasonFormDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { useDealActiveConversations } from '@/hooks/useDealActiveConversations';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export interface LostPhaseOption {
  phase_id: string;
  phase_name: string;
  phase_color: string | null;
}

interface DealLossReasonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fases is_lost do pipeline (usado apenas se houver 2+). */
  lostPhases: LostPhaseOption[];
  /** ID do negócio (habilita a listagem de conversas ativas) */
  dealId?: string | null;
  /** Callback ao confirmar */
  onConfirm: (payload: {
    lossReasonId: string;
    targetPhaseId: string | null;
    lossReasonNote: string | null;
    conversationIdsToClose: string[];
  }) => void;
  isPending?: boolean;
}

export function DealLossReasonModal({
  open,
  onOpenChange,
  lostPhases,
  dealId,
  onConfirm,
  isPending = false,
}: DealLossReasonModalProps) {
  const { activeReasons, isLoading, refetch } = useCrmLossReasons(false);
  const { canCreate } = usePermissions();
  const canCreateReasons = canCreate('crm_param_loss_reasons');

  // Conversas ativas do negócio (lazy: só busca com o modal aberto)
  const { data: activeConversations = [], isLoading: loadingConversations } =
    useDealActiveConversations(dealId ?? null, open);

  // Estado do formulário
  const [reasonId, setReasonId] = useState<string>('');
  const [phaseId, setPhaseId] = useState<string>('');
  const [note, setNote] = useState('');
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  // IDs das conversas selecionadas para encerrar
  const [selectedConversations, setSelectedConversations] = useState<string[]>([]);

  const needsPhaseChoice = lostPhases.length > 1;

  // Refs para evitar que re-renders do pai (ex.: query de conversas) disparem reset
  const lostPhasesRef = React.useRef(lostPhases);
  lostPhasesRef.current = lostPhases;
  // Marca se a pré-seleção de conversas já foi aplicada nesta abertura
  const preselectedRef = React.useRef(false);

  // Reset APENAS na transição fechado -> aberto
  useEffect(() => {
    if (open) {
      const phases = lostPhasesRef.current;
      setReasonId('');
      setPhaseId(phases.length === 1 ? phases[0].phase_id : '');
      setNote('');
      setSelectedConversations([]);
      preselectedRef.current = false;
    }
  }, [open]);

  // Chave estável dos ids das conversas (evita depender do array recriado)
  const conversationIdsKey = activeConversations
    .map((c) => c.conversation_id)
    .join(',');

  // Marca todas as conversas ativas por padrão — uma única vez por abertura
  useEffect(() => {
    if (!open || preselectedRef.current || !conversationIdsKey) return;
    preselectedRef.current = true;
    setSelectedConversations(conversationIdsKey.split(','));
  }, [open, conversationIdsKey]);


  // Alterna a seleção de uma conversa
  const toggleConversation = (id: string) => {
    setSelectedConversations((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleConfirm = () => {
    if (!reasonId) return;
    if (needsPhaseChoice && !phaseId) return;
    onConfirm({
      lossReasonId: reasonId,
      targetPhaseId: needsPhaseChoice
        ? phaseId
        : (lostPhases[0]?.phase_id ?? null),
      lossReasonNote: note.trim() || null,
      conversationIdsToClose: selectedConversations,
    });
  };

  const canConfirm =
    !!reasonId && (!needsPhaseChoice || !!phaseId) && !isPending;


  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-destructive/10">
                <XCircle className="h-5 w-5 text-destructive" />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-base">
                  Marcar negócio como perdido
                </DialogTitle>
                <DialogDescription className="text-xs mt-0.5">
                  Informe o motivo da perda para concluir o negócio.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {/* Motivo (obrigatório) */}
            <div className="space-y-1.5">
              <Label>Motivo da perda *</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Select value={reasonId} onValueChange={setReasonId}>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          isLoading
                            ? 'Carregando motivos...'
                            : activeReasons.length === 0
                              ? 'Nenhum motivo cadastrado'
                              : 'Selecione o motivo'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {activeReasons.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          <div className="flex items-center gap-2">
                            <span
                              className="h-2.5 w-2.5 rounded-full ring-1 ring-border"
                              style={{ backgroundColor: r.color ?? '#9ca3af' }}
                            />
                            {r.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {canCreateReasons && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setReasonDialogOpen(true)}
                    title="Cadastrar novo motivo"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {activeReasons.length === 0 && !isLoading && (
                <p className="text-xs text-muted-foreground">
                  Cadastre pelo menos um motivo em <em>CRM → Motivos de Perda</em>
                  {canCreateReasons ? ' ou clique em + acima.' : '.'}
                </p>
              )}
            </div>

            {/* Fase (só se 2+ fases lost) */}
            {needsPhaseChoice && (
              <div className="space-y-1.5">
                <Label>Fase de perda *</Label>
                <Select value={phaseId} onValueChange={setPhaseId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a fase" />
                  </SelectTrigger>
                  <SelectContent>
                    {lostPhases.map((p) => (
                      <SelectItem key={p.phase_id} value={p.phase_id}>
                        <div className="flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 rounded-full ring-1 ring-border"
                            style={{ backgroundColor: p.phase_color ?? '#9ca3af' }}
                          />
                          {p.phase_name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Conversas ativas do contato (só quando existirem) */}
            {!loadingConversations && activeConversations.length > 0 && (
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                  Conversas ativas deste contato
                </Label>
                <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-md border p-2">
                  {activeConversations.map((c) => (
                    <label
                      key={c.conversation_id}
                      className="flex cursor-pointer items-start gap-2 rounded-sm p-1.5 hover:bg-muted/50"
                    >
                      <Checkbox
                        checked={selectedConversations.includes(c.conversation_id)}
                        onCheckedChange={() => toggleConversation(c.conversation_id)}
                        className="mt-0.5"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {/* Ícone do canal (mesma regra visual da listagem de inboxes) */}
                          {(() => {
                            const ch = getChannelVisual(c.channel_type);
                            return (
                              <span
                                title={ch.label}
                                className={cn(
                                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
                                  ch.bgColor,
                                )}
                              >
                                <ch.Icon className={cn('h-2.5 w-2.5', ch.iconColor)} />
                              </span>
                            );
                          })()}
                          <span className="truncate text-xs font-medium">
                            {c.inbox_name || 'Caixa de entrada'}
                          </span>
                          <Badge variant="outline" className="h-4 px-1 text-[10px]">
                            {c.status === 'open' ? 'Aberta' : 'Pendente'}
                          </Badge>
                        </div>
                        <p className="truncate text-[11px] text-muted-foreground">
                          {c.assigned_agent_name || 'Sem responsável'}
                          {c.last_message_at
                            ? ` · ${formatDistanceToNow(new Date(c.last_message_at), {
                                addSuffix: true,
                                locale: ptBR,
                              })}`
                            : ''}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  As conversas selecionadas serão encerradas ao confirmar a perda.
                </p>
              </div>
            )}

            {/* Observação opcional */}

            <div className="space-y-1.5">
              <Label htmlFor="loss-note">Observação (opcional)</Label>
              <Textarea
                id="loss-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Detalhes adicionais sobre a perda..."
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!canConfirm}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <XCircle className="mr-2 h-3.5 w-3.5" />
                  Confirmar perda
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal encadeado para cadastro rápido de motivo */}
      <LossReasonFormDialog
        open={reasonDialogOpen}
        onOpenChange={setReasonDialogOpen}
        editing={null}
        onSaved={(id) => {
          refetch().then(() => setReasonId(id));
        }}
      />
    </>
  );
}
